import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const STEPS = [
  {
    icon: '🍳',
    title: 'Welcome to dePartie',
    body: 'Your personal kitchen command centre. Plan meals, track inventory, generate recipes with AI, and stay on top of your nutrition — all in one place.',
    cta: null,
    ctaPath: null,
  },
  {
    icon: '📦',
    title: 'Inventory',
    body: 'Log everything in your pantry, fridge, and freezer. dePartie tracks expiry dates, alerts you before food goes bad, and can scan barcodes to add items instantly.',
    cta: 'Go to Inventory',
    ctaPath: '/inventory',
  },
  {
    icon: '📖',
    title: 'Recipes',
    body: 'Save your favourite recipes with ingredients, steps, and full macro breakdowns. Cooking a recipe automatically deducts ingredients from your Inventory.',
    cta: 'Go to Recipes',
    ctaPath: '/recipes',
  },
  {
    icon: '📅',
    title: 'Meal Planner',
    body: 'Plan your week at a glance. Drag recipes onto any day and meal slot, then auto-generate a shopping list for everything you\'re missing.',
    cta: 'Go to Meal Planner',
    ctaPath: '/meal-planner',
  },
  {
    icon: '🤖',
    title: 'AI Tools',
    body: 'Stuck on what to cook? Use Recipe AI to generate a new recipe from any ingredients, or let Meal Synthesizer build your whole week\'s plan from your pantry and macro targets.',
    cta: 'Try Recipe AI',
    ctaPath: '/ai-recipe',
  },
  {
    icon: '👥',
    title: 'Community',
    body: 'Browse dishes shared by other home chefs, save recipes you love directly to your collection, and share your own creations with the brigade.',
    cta: 'Browse Community',
    ctaPath: '/community',
  },
  {
    icon: '🏅',
    title: 'Brigade & Marks',
    body: 'Earn marks as you cook, log workouts, and hit nutrition goals. Climb the brigade ranks from Commis to Chef de Partie and unlock new features along the way.',
    cta: 'View Brigade',
    ctaPath: '/brigade',
  },
  {
    icon: '✨',
    title: 'Create your free account',
    body: 'Sign up to sync your data across devices, join the community, and unlock cloud backup. Free forever — upgrade only when you want AI features.',
    cta: 'Sign up free',
    ctaPath: '/settings',
    isLast: true,
  },
];

const OnboardingTour = ({ onDismiss }) => {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();
  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  const handleCta = () => {
    if (current.ctaPath) {
      onDismiss();
      navigate(current.ctaPath);
    }
  };

  const handleNext = () => {
    if (isLast) {
      onDismiss();
    } else {
      setStep(s => s + 1);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(14, 12, 10, 0.82)',
        backdropFilter: 'blur(4px)',
        padding: '16px',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onDismiss(); }}
    >
      <div
        style={{
          background: 'var(--surface-raised)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border-color)',
          width: '100%',
          maxWidth: '440px',
          padding: '32px 28px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '0',
          boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Welcome tour"
      >
        {/* Skip */}
        <button
          onClick={onDismiss}
          style={{
            alignSelf: 'flex-end',
            background: 'none',
            border: 'none',
            color: 'var(--text-muted)',
            fontSize: '13px',
            cursor: 'pointer',
            padding: '0 0 16px',
            lineHeight: 1,
          }}
        >
          Skip tour ×
        </button>

        {/* Icon */}
        <div style={{ fontSize: '48px', textAlign: 'center', marginBottom: '16px', lineHeight: 1 }}>
          {current.icon}
        </div>

        {/* Title */}
        <h2 style={{
          margin: '0 0 10px',
          fontSize: '22px',
          fontWeight: '700',
          color: 'var(--text-primary)',
          textAlign: 'center',
          fontFamily: '"Fraunces", Georgia, serif',
          fontVariationSettings: "'opsz' 32",
          lineHeight: 1.25,
        }}>
          {current.title}
        </h2>

        {/* Body */}
        <p style={{
          margin: '0 0 24px',
          fontSize: '14px',
          lineHeight: '1.65',
          color: 'var(--text-secondary)',
          textAlign: 'center',
        }}>
          {current.body}
        </p>

        {/* Progress dots */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginBottom: '24px' }}>
          {STEPS.map((_, i) => (
            <button
              key={i}
              onClick={() => setStep(i)}
              aria-label={`Go to step ${i + 1}`}
              style={{
                width: i === step ? '20px' : '6px',
                height: '6px',
                borderRadius: '3px',
                border: 'none',
                background: i === step ? 'var(--saffron)' : 'var(--border-color)',
                cursor: 'pointer',
                padding: 0,
                transition: 'width 0.2s, background 0.2s',
              }}
            />
          ))}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '10px' }}>
          {step > 0 && (
            <button
              onClick={() => setStep(s => s - 1)}
              className="btn btn-secondary"
              style={{ flex: 1 }}
            >
              Back
            </button>
          )}
          {current.cta && !current.isLast && (
            <button
              onClick={handleCta}
              className="btn btn-secondary"
              style={{ flex: 1 }}
            >
              {current.cta}
            </button>
          )}
          <button
            onClick={current.isLast ? handleCta : handleNext}
            className="btn btn-primary"
            style={{ flex: step === 0 ? 1 : current.cta && !current.isLast ? 'none' : 1 }}
          >
            {isLast
              ? (current.cta || 'Get started')
              : step === 0
              ? 'Take the tour →'
              : 'Next →'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
